// Privacy Policy text, copied word for word from the app (LegalModal.tsx on
// redesign/legal-20261019) via redesign-one-brain-plan/legal-20261019/website/privacy.txt.
// Keep it identical to the app: replace the whole string when the app text changes.
// Rendered by components/legal/PlainTextDocument.jsx.
const text = `Privacy Policy – Thryvin'

Last updated: 19 October 2026
Effective date: 19 October 2026

This policy explains, in plain English, what information Thryvin' collects, why we use it, who we share it with, how long we keep it and what you can do about it. "Thryvin'", "we" and "us" mean Jake West, trading as Thryvin'. It covers the Thryvin' app (the "App") and our website.

We follow UK data protection law: the UK GDPR and the Data Protection Act 2018. If you live in the EU or in California, you also have the rights in Section 12. This policy also meets Apple App Store or Google Play requirements.


THE SHORT VERSION

• We use your information to run your training, food tracking, coach and Community. We don't sell it, and we don't use it for adverts.
• AI features only send information to our AI provider, OpenAI, if you switch AI on. You can switch it off at any time.
• Health details (injuries, allergies, diet, pregnancy) are optional. We only use them if you agree, and only to make your plan and food suit you. You agree to the food ones with a tick when you set up Nutrition.
• Pregnancy and new-baby details are only saved after you say yes, and only used to keep your training, meals and coach in line with NHS guidance.
• Thryvin' gives general guidance, not medical advice. AI can get things wrong (see our Terms).
• Community is optional and for adults only. Your body, health, food, injury and coach information isn't shown there.
• You can download your data, or delete your account, in the App at any time.


1. WHO WE ARE

Thryvin' is run by a sole trader in the UK:

Jake West, trading as Thryvin'
11 Netley Close, BH15 3NW, United Kingdom
Email: support@thryvin.app

Under data protection law, Jake West is the "data controller". That means he is responsible for your information.


2. WHAT WE COLLECT

2.1 Your account
• Your name and email address
• Your password. We only keep a scrambled version (a "hash"), so we can't read it.
• Your date of birth or age, to check you're 18 or over
• Your phone's time zone, so your plan, check-ins, reminders, coach messages and monthly credits follow your local day. The App sends it with its requests, and we keep the latest one with your account.

2.2 Training and progress
• Height, weight and body measurements, if you add them
• Your goals, experience, training preferences, equipment and schedule
• Injuries, if you tell us about them
• Your workouts, sets, reps, weights, times, extra activities, records, favourites and progress
• Check-in answers and scores, and (Pro) your weight and optional progress photos

Injuries are health information. The law calls this "special category data" and gives it extra protection (Article 9). We only use it with your explicit consent (Article 9(2)(a)). The question tells you what it's for, and you agree by choosing to answer. We only use it to adapt your workouts and to avoid exercises that might make an injury worse. Thryvin' doesn't diagnose or treat injuries. You can remove it at any time.

2.3 Food and nutrition
• Food and water logs, saved meals, your own recipes, meal plans, shopping lists, pantry, meal-prep boxes, food check-ins and scan history
• Your food targets and the details used to work them out (such as height, weight, age, sex, activity and goal)
• Allergies, intolerances and diet choices (such as vegetarian, halal or kosher)

Allergies and intolerances are health information, and some diet choices can show your religion. So we treat them as special category data too. We only use them with your explicit consent (Article 9(2)(a)). You give it with the tick box when you set up Nutrition, and we record the date and the version of the wording you agreed to. We use them to tailor your food and to try to warn you about ingredients. Our checks can miss things, so always read the label. These answers are optional, and you can change or remove them in your nutrition settings. If you remove them, we can't check food against them. Your food logs aren't shown in Community and are never posted for you.

2.3a Pregnancy and after the birth (optional)
• If you choose to tell us, we keep: whether you're pregnant or have had a baby in the last year, your due date or weeks pregnant, your baby's date of birth and how they were born, whether your midwife or GP has mentioned complications, whether you're breastfeeding, whether your tummy muscles have been checked, and whether your GP or midwife has said exercise is OK.
• This is health information (special category data). We only save it after you say yes on a consent card, or when you fill in Pregnancy & new baby in Settings. We only use it to keep your training, meals and coach tips in line with general NHS guidance. Thryvin' doesn't give medical or midwife care.
• If AI is switched on, the coach and the plan builder use these details (Section 4).
• It isn't shown in Community. You can change or remove it at any time in You → Settings → Pregnancy & new baby, and we then stop using it.

2.4 Photos, voice and typed food descriptions
• Food and fridge photos: sent for analysis only when you take or choose one, and only if AI is on. Thryvin does not keep these photos.
• Describing food in words: we look it up in our own food database first. Only the parts it can't match go to OpenAI.
• Voice (talking to your coach, describing food, adding to your shopping list): the recording goes to OpenAI to turn it into text. We delete the temporary audio file on our server once the text comes back.
• Spoken coach replies: if you ask the coach to speak, the reply text goes to OpenAI to turn it into speech.
• Check-in progress photos (Pro): kept with your check-in and read by AI to tailor your plan. We take steps to keep them private. They're deleted when you delete your account.
• Your profile picture on the You page and the share pictures you make stay on your phone. We don't upload them.
• Form check (beta) runs on your phone. The video isn't uploaded.

2.5 Barcode scanner and food search
• When you scan a product, only the barcode number is sent to Open Food Facts. It goes from our server, not your phone.
• If our own food database can't answer a search, the search words go to Open Food Facts too.
• Nothing that identifies you is sent with them.

2.6 Coach
• Your messages to the coach and its replies
• Coach settings (name, personality, reply length and so on)
• What the coach has learned about your preferences and training, and coach cards you save

2.7 Community (only if you join)
• Your nickname, avatar or optional profile photo, Community settings, and a note that you confirmed you're 18+ with the date (not a second copy of your birthday)
• Posts, captions, comments, reactions and Live reaction photos, workouts and recipes you share, friends and friend requests, people you block and reports you make

2.8 Connected health apps (optional)
If you connect Apple Health, Health Connect, Strava or another supported app, you choose what to share. We keep what you allow (such as steps, active energy, sleep and sleep stages, heart rate, resting heart rate, heart-rate variability and workouts, with their dates and source) for your Health Hub charts and history. The App works without it. Imported workouts wait for you to review them and never change your plan by themselves. Missing readings are treated as missing, not as zero. If you disconnect, syncing stops. History already imported stays until you delete it or your account.

2.9 Collected automatically
• Device details: model, operating system, app version, language and time zone
• Technical details: your IP address, used for security and to stop abuse. We don't keep it long term in a way that identifies you.
• Error and crash reports. These are linked to your account number (never your email or name) so we can find and fix problems for you. We keep them in our own database for 30 days, and in Sentry. The list of screens and actions that comes with them doesn't include your name.
• Push notification tokens, if you allow notifications
• Records of what you agreed to: which version of our Terms, this Privacy Policy and the Nutrition acknowledgement you agreed to, and when

2.10 What we don't collect
• We don't collect your precise location or your contacts.
• We only use your camera, photos and microphone when you choose a feature that needs them: barcode scanning, meal and fridge photos, check-in photos, Community Live reactions and profile photos, share pictures, form check, talking to your coach and describing food by voice.
• We never see or store your payment card details (see Section 7).


3. WHY WE USE YOUR INFORMATION

The law says we need a reason (a "lawful basis") for each use. Ours are:
• To give you the service you signed up for (contract): your account, plans, workouts, food tracking, the coach, the Community features you use, Pro and plan credits
• Your explicit consent: for injuries, allergies, intolerances and diet choices, pregnancy and new-baby details, AI processing by OpenAI, and health-aware coaching. You can withdraw it at any time. This doesn't undo what was done before, and some features can't work without it (for example, we can't check food against allergies you've removed).
• Our legitimate interests: keeping Thryvin' secure, stopping fraud and abuse, fixing errors and crashes, improving the App, and running the automatic Community safety check
• Legal duties: dealing with illegal content (including reporting child sexual abuse material to the authorities), keeping tax records and answering lawful requests

We use your information to:
• Create and run your account
• Build and adapt your training plan and, if you use Nutrition, your food targets and meal plans
• Run the coach and remember your preferences
• Track your progress, records and history
• Run Community: friends, feeds, reactions, safety checks, reports and blocking
• Run subscriptions and Pro features, including plan credits
• Send service emails (such as verification, password resets and important updates) and the notifications you allow
• Answer support requests
• Fix bugs and crashes, and stop abuse or fraud
• Send marketing emails, but only if you opt in. You can unsubscribe at any time.

We don't sell your personal information, use it for other companies' adverts, or share your coach chats with advertisers or data brokers.


4. AI (OPENAI)

• AI only runs if you allow it on the AI permission screen. When we change that wording, we ask everyone again. We last did this on 15 October 2026 to add food.
• If you allow it, we send OpenAI what it needs to write coach replies, plans and coach messages: your messages, goals, training preferences, logged workouts, injuries and earlier coach chats. Your first name may be included so the coach can talk to you by name.
• For food, we also send your food and water logs, targets, allergies, intolerances and diet choices, to create meal plans, recipe and fridge ideas and food tips.
• If you've saved pregnancy or new-baby details, we send your stage (for example "24 weeks pregnant" or "8 weeks after a C-section") and the related answers, so plans, meals and coach replies follow NHS guidance.
• Voice recordings go to OpenAI when you use voice. Food, fridge and check-in photos go only when you add them. Typed food descriptions go only for the parts our own database can't match. If you ask the coach to speak, its reply text goes to OpenAI to make the voice.
• We don't send OpenAI your email address or contact details.
• OpenAI processes data in the US. By default it doesn't use this data to train its models. It may keep it for a limited time to check for abuse, under its own terms.
• AI answers go through our own safety rules (for example allergy checks, calorie minimums, and injury and pregnancy limits). But no person reviews each reply before you see it, and it can still be wrong.
• Without AI, you can still use your existing workouts, Health Hub, food search, barcode scanning and manual food logging.
• You can withdraw permission at any time in You → Settings → Permissions & privacy. This stops future AI use. It doesn't delete what was already sent: to do that, delete your account or contact us.

Health-aware coaching is a separate, optional choice. If you say yes, short summaries of your connected activity, steps, active energy, sleep, resting heart rate and heart-rate variability for up to 30 days may go to OpenAI for coaching, check-ins, plans and coach messages. It needs the general AI permission too. Saying no keeps your Health Hub charts and history. Allowing health access on your phone, or creating an account, never counts as saying yes to this.

The automatic Community safety check isn't part of the AI permission. It's explained in Section 5.3.


5. COMMUNITY

5.1 What other people can see
• Community is optional and only for people aged 18 or over. You accept the Community Guidelines when you join.
• Friends (and, for posts you make public, other members) can see your nickname, avatar, Community card, posts, comments and reactions.
• Your profile photo is optional, must be of you, and only your friends see it. Everyone else sees your initials.
• People can only find you by nickname if you switch on "Let people find me".
• Community doesn't show your body measurements, health readings, food logs, allergies, injuries, coach chats, email or contact details.

5.2 Live reaction photos
• Taken in the App with the front camera only. Hidden details in the photo (such as EXIF and GPS location) are removed.
• Seen only by the post owner and your friends. Anyone else sees an emoji instead.
• Viewing links expire after 1 hour.
• The photo is deleted after 7 days and the reaction becomes an emoji.
• We don't use face recognition.
• Phones can't stop someone taking a screenshot. Only react with photos you're happy for friends to see.

5.3 The automatic safety check
• Every Live reaction photo and Community profile photo, and the text people post (captions, comments, text posts and shared recipes), is checked automatically by OpenAI's moderation service.
• This runs for everyone in Community, whether or not AI coaching is switched on. It's a safety measure, based on our legitimate interest in keeping members safe and on our legal duties, not on consent.
• Only the photo or text being checked is sent, with no name or account details.
• Nicknames and posts also go through our own word filter, which blocks rude words, links, emails, phone numbers and @handles.
• If the check isn't available, photos stay hidden and shared recipes wait until it is.
• Automatic checks can make mistakes either way, so please report anything that slips through.

5.4 Reports, moderation and blocking
• Reports are reviewed by a person at Thryvin'. We keep a copy of reported content for the review.
• Photos the safety check rejects are kept privately for 30 days as evidence, then deleted. Photos held after a report are kept privately for up to 90 days.
• Suspected child sexual abuse material is kept as evidence and reported to the National Crime Agency (NCA) and the Internet Watch Foundation (IWF).
• If your content is removed, we'll tell you why. You can appeal within 14 days by replying or emailing support@thryvin.app.
• Blocking hides you and the other person from each other. They aren't told.
• Community notifications are sent through Expo, Apple and Google. You can switch each type on or off in Community & privacy.

5.5 Leaving, deleting and exporting
• Leaving Community hides your posts from other members.
• "Delete my Community data" deletes it and keeps your Thryvin account.
• You can export your Community data from Community & privacy. It's also in Download my data.
• Deleting your account deletes all of it. Reports you made stay, without your name, so the review can finish.


6. FOOD DATABASES AND CREDITS

• UK foods: CoFID (McCance and Widdowson's composition of foods), © Crown copyright. Contains public sector information licensed under the Open Government Licence v3.0.
• US foods: USDA FoodData Central, which is in the public domain.
• Branded products: Open Food Facts, available under the Open Database Licence (ODbL), with product images under Creative Commons Attribution-ShareAlike. Its data is added by volunteers and can be wrong, so always check it against your pack.
• Open Food Facts is an independent database, not one of our service providers. It only receives barcode numbers and search words (Section 2.5).


7. WHO WE SHARE INFORMATION WITH

We use a small number of service providers (the law calls them "processors") to run Thryvin'. Each one only gets what it needs, under its own terms and data protection agreement.

• Neon: our main database (your account, plans, workouts, food, coach and Community data). EU / US.
• Railway: runs our server (everything our server handles). US.
• OpenAI: AI coach replies, plans, meal and fridge ideas, food and fridge photos, describing food, voice to text, spoken replies and check-in photos (only with AI permission, Section 4), plus the Community safety check (Section 5.3). US.
• Cloudflare: hosts our website. Cloudflare (R2 storage) also keeps Live reaction photos for 7 days, and Community profile photos until you remove them, leave Community or delete your account. US / EU.
• Cloudinary: stores Pro check-in progress photos and hosts our exercise videos (so it sees your device's IP address when a video plays). US.
• RevenueCat: manages subscriptions (account number, subscription status, purchase events). US.
• Apple and Google: app downloads, in-app purchases and push notifications. They take payment, so we never see your card. US / worldwide.
• Expo (EAS): app delivery and push notifications (push tokens, device identifiers). US.
• Sentry: crash and error reports with app and device details and your account number (not your email or name), so we can fix problems. EU / US.
• Resend: sends our emails (your email address and the email). US / EU.
• Strava, Apple Health and Health Connect: only if you connect them. They show their own permission screens, and we only get what you allow.
• PostHog: visitor statistics on our website (thryvin.app) only, not in the App. It doesn't set cookies unless you agree. EU / US.

We update this list when it changes.


8. SENDING INFORMATION ABROAD

Some of these providers (including OpenAI, Railway, Cloudflare, Cloudinary, RevenueCat, Sentry, Expo and Resend) are in, or use servers in, the United States. When your information leaves the UK or the European Economic Area, we use the protections UK law allows, such as:
• the UK-US Data Bridge (the UK Extension to the EU-US Data Privacy Framework), where the provider has signed up to it
• the UK International Data Transfer Agreement (IDTA), or the EU Standard Contractual Clauses with the UK Addendum
• each provider's data processing agreement, which limits what it may do with your information

Email support@thryvin.app if you'd like a copy of these protections.


9. AUTOMATED DECISIONS

• The coach uses automated processing to suggest workouts, adapt your plan, suggest food and answer your messages. This is general fitness and food guidance, not medical advice. It doesn't make decisions with legal or similarly serious effects on you.
• The Community safety check can automatically hide or block a photo or post. Three failed photo checks in a day pause photo reactions for 24 hours.
• You can ask a person to look again at any automatic outcome that affects you: report it in the App or email support@thryvin.app.


10. HOW LONG WE KEEP YOUR INFORMATION

• Account, training, check-ins, food logs, coach chats, connected health history and Community content: until you delete them or your account
• Food and fridge photos: not kept
• Voice recordings: deleted from our server once turned into text
• Live reaction photos: 7 days
• Photos rejected by the safety check: 30 days
• Reported photos held for review, and copies kept for a report: up to 90 days after the report
• Friend requests: expire after 30 days
• Error, crash and diagnostic reports in our own database: 30 days, including reports linked to your account number after you delete your account. Sentry keeps its reports for the period set in our Sentry account.
• Records of what you agreed to (Terms, Privacy Policy, Nutrition acknowledgement): for as long as you have an account
• Emails waiting to be sent: up to 30 days
• Records of warnings, suspensions and bans: as long as needed to enforce them and handle appeals
• Suspected child sexual abuse material: as long as the authorities require
• Purchase and tax records (including subscription events from the stores, linked to your account number): as long as the law requires, usually 6 years
• Database backups: short-term restore copies, kept for a limited time and then overwritten

When you delete your account, we delete your information from our live systems. A few records stay for a limited time: error reports (up to 30 days), purchase records for tax, and anything else the law makes us keep. We send one email to confirm the deletion. Backup copies, and copies held by our service providers, are removed on their normal schedule.


11. KEEPING YOUR INFORMATION SAFE

We work to protect your information. For example, we:
• Keep passwords only in scrambled (hashed) form
• Encrypt information as it travels between your phone and our servers (TLS)
• Limit who can access our systems
• Use links that expire for Community photos

No system is completely secure, so we can't promise your information will never be accessed without permission. If there's a data breach that puts your rights at risk, we'll tell the UK Information Commissioner's Office (ICO) and, where needed, you, as the law requires.


12. YOUR RIGHTS

12.1 Your rights under UK and EU law

You have the right to:
• Access: get a copy of your information
• Correction: fix information that's wrong
• Deletion: have your information deleted
• Restriction: limit how we use your information
• Portability: get your information in a format you can take elsewhere
• Object: object to uses based on our legitimate interests
• Withdraw consent: where we rely on your consent
• Human review: ask a person to look at an automatic outcome (Section 9)

Download my data: You → Settings → Privacy & AI → Download my data gives you one file (JSON) with the information Thryvin holds about your account. For anything else, email support@thryvin.app. We'll reply within one month, as the law requires. If a request is complicated, it can take longer, and we'll tell you why.

You can complain to a data protection regulator. In the UK that's the Information Commissioner's Office (ico.org.uk). In the EU, contact your local regulator. We'd like the chance to help first, so please email us.

12.2 Rights in California (CCPA)

If you live in California, you have the right to:
• Know what personal information we collect about you
• Know whether we sell or share it (we don't)
• Ask us to delete it
• Opt out of it being sold or shared (we don't sell it or share it for adverts)
• Not be treated differently for using these rights

Email support@thryvin.app to use these rights.

12.3 Deleting your account

You can delete your account at any time in the App: You → Settings → Delete account. This deletes your account and information from our live systems, including progress photos and connected-app links (see Section 10 for the few records we keep for a while). Deleting your account doesn't cancel a subscription bought through Apple App Store or Google Play. Cancel it in your store account so you aren't charged again. You can also email support@thryvin.app to ask us to delete your account.


13. CHILDREN

Thryvin' is only for people aged 18 and over, and Community is 18+ only. We don't knowingly collect information from anyone under 18. If you think a child has given us information, email support@thryvin.app and we'll delete it.


14. COOKIES AND TRACKING

The App doesn't use cookies. It uses device identifiers and our own error reports (Section 2.9) so it works, stays secure and can be fixed. We don't use advertising trackers, and we don't track you across other companies' apps or websites.

Our website (thryvin.app) uses PostHog for basic visitor statistics. It won't set non-essential cookies unless you agree.


15. CHANGES TO THIS POLICY

When we change this policy, we'll update the "Last updated" date. If the change is significant, we'll tell you in the App or by email. We keep a record of which version you agreed to. If a change is significant, or affects something you agreed to (such as the AI permission or the Nutrition acknowledgement), the App asks you to read and agree again.


16. CONTACT US

Questions, worries or requests about this policy or your information:

Email: support@thryvin.app
Post: Jake West (Thryvin'), 11 Netley Close, BH15 3NW, United Kingdom

Thryvin' is run by Jake West, a sole trader in the United Kingdom.
`;

export default text;
